import Settings from 'configs/Settings';
import { setThumborSignedURL } from 'helpers/ImageFile';
import Number from 'helpers/Number';

const EDITOR_GRAY_AREA_PADDING = 110;
const SAFETY_AREA = 0.05;
const TOOLBAR_PADDING = 40;
const HYPOTENUSE = 10;
const FILES_URL_OLD = `${Settings.api.fileClub}/public/v4/files`;

export const CANVAS_FLOAT_PRECISION = 4;

class Canvas {
  getDifferenceBoundingNormal(object, objectBoundingRect, property) {
    return object[property] - objectBoundingRect[property];
  }

  getCanvasAvailableSpace(object, objectBoundingRect, property) {
    return object.canvas[property] - objectBoundingRect[property];
  }

  getCanvasMaxRight(object, objectBoundingRect) {
    return (
      this.getCanvasAvailableSpace(object, objectBoundingRect, 'width') +
      this.getDifferenceBoundingNormal(object, objectBoundingRect, 'left')
    );
  }

  getCanvasMaxBottom(object, objectBoundingRect) {
    return (
      this.getCanvasAvailableSpace(object, objectBoundingRect, 'height') +
      this.getDifferenceBoundingNormal(object, objectBoundingRect, 'top')
    );
  }

  getScale(object, property) {
    return object.canvas[property] / object[property];
  }

  getMaxHorizontalScale(object) {
    return (
      this.getScale(object, 'width') -
      this.getScale(object, 'width') * SAFETY_AREA
    );
  }

  getMaxVerticalScale(object) {
    return (
      this.getScale(object, 'height') -
      this.getScale(object, 'width') * SAFETY_AREA
    );
  }

  degreeToRadians(degrees) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return (degrees * Math.PI) / 180;
  }

  getAdjacentLeg(angle, hypotenuse) {
    const radians = this.degreeToRadians(angle);

    return Math.cos(radians) * hypotenuse;
  }

  getOppositeLeg(angle, hypotenuse) {
    const radians = this.degreeToRadians(angle);

    return Math.sin(radians) * hypotenuse;
  }

  getDeleteButtonLeftPosition(object) {
    const topRightCorner = object.oCoords.mr;
    const adjacent = this.getAdjacentLeg(object.angle, HYPOTENUSE);

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return topRightCorner.x + EDITOR_GRAY_AREA_PADDING + 2.5 * adjacent;
  }

  getDeleteButtonTopPosition(object) {
    const topRightCorner = object.oCoords.mr;
    const opposite = this.getOppositeLeg(object.angle, HYPOTENUSE);

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return topRightCorner.y + TOOLBAR_PADDING + 3 * opposite;
  }

  getDeleteButtonPosition(object, setRemoveButtonPositionToStore) {
    const objectBoundingRect = object.getBoundingRect();
    let left = null;
    let top = null;

    if (this.isObjectLargerThanCanvas(objectBoundingRect, object.canvas)) {
      return;
    }

    if (!this.doesObjectEscapeHorizontally(objectBoundingRect, object.canvas)) {
      left = this.getDeleteButtonLeftPosition(object);
    }

    if (!this.doesObjectEscapeVertically(objectBoundingRect, object.canvas)) {
      top = this.getDeleteButtonTopPosition(object);
    }

    setRemoveButtonPositionToStore({ left, top, display: 'block' });
  }

  doesObjectScaleHorizontalOutside(object, canvas) {
    return object.width >= canvas.width * (1 - SAFETY_AREA);
  }

  doesObjectScaleVerticalOutside(object, canvas) {
    return object.height >= canvas.height * (1 - SAFETY_AREA);
  }

  doesObjectEscapeHorizontally(objectBoundingRect, canvas) {
    return (
      this.doesObjectEscapeFromRigth(objectBoundingRect, canvas) ||
      this.doesObjectEscapeFromLeft(objectBoundingRect)
    );
  }

  doesObjectEscapeVertically(objectBoundingRect, canvas) {
    return (
      this.doesObjectEscapeFromTop(objectBoundingRect) ||
      this.doesObjectEscapeFromBottom(objectBoundingRect, canvas)
    );
  }

  doesObjectEscapeFromLeft(objectBoundingRect) {
    return objectBoundingRect.left < 0;
  }

  doesObjectEscapeFromRigth(objectBoundingRect, canvas) {
    return objectBoundingRect.left + objectBoundingRect.width > canvas.width;
  }

  doesObjectEscapeFromTop(objectBoundingRect) {
    return objectBoundingRect.top < 0;
  }

  doesObjectEscapeFromBottom(objectBoundingRect, canvas) {
    return objectBoundingRect.top + objectBoundingRect.height > canvas.height;
  }

  isObjectLargerThanCanvas(object, canvas) {
    return object.height > canvas.height || object.width > canvas.width;
  }

  retainObjectInsideCanvas(object) {
    const objectBoundingRect = object.getBoundingRect();
    const escapedFromTop = this.doesObjectEscapeFromTop(objectBoundingRect);
    const escapedFromLeft = this.doesObjectEscapeFromLeft(objectBoundingRect);
    const escapedFromBottom = this.doesObjectEscapeFromBottom(
      objectBoundingRect,
      object.canvas
    );
    const escapedFromRigth = this.doesObjectEscapeFromRigth(
      objectBoundingRect,
      object.canvas
    );

    if (escapedFromTop || escapedFromLeft) {
      object.top = Math.max(
        object.top,
        this.getDifferenceBoundingNormal(object, objectBoundingRect, 'top')
      );
      object.left = Math.max(
        object.left,
        this.getDifferenceBoundingNormal(object, objectBoundingRect, 'left')
      );
    }

    if (escapedFromBottom || escapedFromRigth) {
      object.top = Math.min(
        object.top,
        this.getCanvasMaxBottom(object, objectBoundingRect)
      );
      object.left = Math.min(
        object.left,
        this.getCanvasMaxRight(object, objectBoundingRect)
      );
    }
  }

  alignObjectsInCenter(canvas) {
    const objects = canvas.getObjects();

    objects.forEach((item) => {
      item.centerH();
      item.set('textAlign', 'center');
      item.setCoords();
    });

    canvas.renderAll();
  }

  defineCanvasBoundaries(canvas, setRemoveButtonPositionToStore, e) {
    const object = e.target;

    object.setCoords();

    this.retainObjectInsideCanvas(object, canvas);
    this.getDeleteButtonPosition(object, setRemoveButtonPositionToStore);

    object.setCoords();
  }

  preventStickToBoundaries(object) {
    const SCALE_FIX = 0.01;

    object.lastScaleX -= SCALE_FIX;
    object.lastScaleY -= SCALE_FIX;
  }

  setMaxScale(object) {
    object.scaleX = object.lastScaleX;
    object.scaleY = object.lastScaleY;
  }

  saveLastValidScale(object) {
    object.lastScaleX = object.scaleX;
    object.lastScaleY = object.scaleY;
  }

  defineScaleBoundaries(canvas, setRemoveButtonPositionToStore, e) {
    const object = e.target;
    const objectBoundingRect = object.getBoundingRect();

    if (
      this.doesObjectScaleHorizontalOutside(objectBoundingRect, canvas) ||
      this.doesObjectScaleVerticalOutside(objectBoundingRect, canvas)
    ) {
      const isDecreasingScaleInsideCanvas =
        object.scaleX < object.lastScaleX || object.scaleY < object.lastScaleY;

      if (isDecreasingScaleInsideCanvas) {
        this.preventStickToBoundaries(object);
      }

      this.setMaxScale(object);
    } else {
      this.saveLastValidScale(object);
    }

    object.setCoords();
    this.retainObjectInsideCanvas(object, canvas);
    object.setCoords();

    this.getDeleteButtonPosition(object, setRemoveButtonPositionToStore);
  }

  selectObject(canvas, setRemoveButtonPositionToStore) {
    const activeObject = canvas.getActiveObject();

    this.getDeleteButtonPosition(activeObject, setRemoveButtonPositionToStore);
  }

  resetCursor(canvas) {
    canvas.defaultCursor = 'default';
  }

  isITextType({ type = '' }) {
    return type === 'i-text';
  }

  isImage({ type = '' }) {
    return type === 'image';
  }

  isTextMergedTag({ text = '' }) {
    return this.isMergedTag(text);
  }

  isMergedTag(text) {
    return text.startsWith('*|') && text.endsWith('|*');
  }

  mergedTagValue(text = '') {
    return text.replace('*|', '').replace('|*', '');
  }

  moveElementAccordingNewWidth(item, canvas) {
    const originalWidth = item.width;

    canvas.renderAll();

    item.width = originalWidth;

    canvas.renderAll();
  }

  formatCanvasImageURL(src) {
    const url = this.isMergedTag(src)
      ? `${FILES_URL_OLD}/${this.mergedTagValue(src)}`
      : src;

    return setThumborSignedURL(url);
  }

  swapTranslation(objs, canvas) {
    let swap = null;
    const templateJson = canvas.toJSON();

    objs &&
      objs.forEach((item, idx) => {
        const canSwapTranslate =
          this.isITextType(item) &&
          this.isTextMergedTag(item) &&
          item.mergedTag;

        if (canSwapTranslate) {
          templateJson.objects[idx].text = item.mergedTag;

          swap = item.text;
          item.text = item.mergedTag;
          item.mergedTag = swap;

          this.moveElementAccordingNewWidth(item, canvas);
        }
      });

    canvas.renderAll();
    const templateSvg = canvas.toSVG();

    return { templateJson, templateSvg };
  }

  getTranslatedCertificate(canvas, objs) {
    const { templateSvg, templateJson } = this.swapTranslation(objs, canvas);

    canvas.renderAll();

    this.swapTranslation(objs, canvas);
    canvas.renderAll();

    return { templateSvg, templateJson };
  }

  loadCanvasData(canvas, data) {
    return new Promise((resolve) => {
      canvas.loadFromJSON(data, () => resolve(canvas));
    });
  }

  calculatePositionOffset(scale, canvasLength, imgLength) {
    const offset = -(imgLength * scale - canvasLength) / 2;

    return offset > 0
      ? Number.ceil(offset, CANVAS_FLOAT_PRECISION)
      : Number.floor(offset, CANVAS_FLOAT_PRECISION);
  }

  getBackgroundConfigToFitCanvas(img, canvas) {
    const { width, height } = img;

    const scaleX = canvas.width / width;
    const scaleY = canvas.height / height;

    const ceiledScaleX = Number.ceil(scaleX, CANVAS_FLOAT_PRECISION);
    const ceiledScaleY = Number.ceil(scaleY, CANVAS_FLOAT_PRECISION);

    const leftOffset = this.calculatePositionOffset(
      ceiledScaleX,
      canvas.width,
      width
    );
    const topOffset = this.calculatePositionOffset(
      ceiledScaleY,
      canvas.height,
      height
    );

    return {
      scaleX: ceiledScaleX,
      scaleY: ceiledScaleY,
      originX: 'left',
      originY: 'top',
      top: topOffset,
      left: leftOffset
    };
  }

  backgroundConfigIsValid(backgroundConfig) {
    return Boolean(
      backgroundConfig &&
        !isNaN(backgroundConfig.scaleX) &&
        !isNaN(backgroundConfig.scaleY) &&
        !isNaN(backgroundConfig.top) &&
        !isNaN(backgroundConfig.left)
    );
  }
}

export default new Canvas();
